import React, { Fragment, useEffect, useState } from 'react'
import { Edit, Delete, Visibility } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { FilterComponent } from '../../components/_WMS/FilterComponent'
import { GenericTableV2 } from '../../components/_EOS/GenericTable/GenericTableV2'
import { Modal } from '../../components/_EOS/Modal'
import { SnackBar } from '../../components/SnackBar/Snackbar'

import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { useModal } from '../../hooks/useModal'
import { useSnackbar } from '../../hooks/useSnackbar'

import { filterOptions } from './config/filterOptions'
import { getColumnsConfig } from './config/tableColumns'
import {
  getProductExternalReferences,
  createProductExternalReference,
  updateProductExternalReference,
  deleteProductExternalReference
} from './config/api'
import { ProductExternalReferenceForm } from './components/ProductExternalReferenceForm'
import { ViewProductExternalReference } from './components/ViewProductExternalReference'

import { ProductExternalReference } from '../../interfaces/Producto/ProductExternalReference'
import { Filter } from '../../components/_WMS/FilterComponent/types'
import { Status } from '../../hooks/useFetchState'
import { ApiResponse } from '../../typings/ApiResponse'
import { GenericOrderBy } from '../../components/_EOS/Table/types'
import { calculatePageAfterDelete } from '../../utils/paginationHelpers'

export const ProductExternalReferencePage: React.FC = () => {
  const { callApi, state } = useApi<ApiResponse<ProductExternalReference>>()
  const pagination = usePagination(5)
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const [orderBy, setOrderBy] = useState<
    GenericOrderBy<ProductExternalReference>
  >({
    field: 'name' as keyof ProductExternalReference,
    direction: 'asc'
  })

  const createModal = useModal('Crear Marca PIM')
  const editModal = useModal<ProductExternalReference>('Editar Marca PIM')
  const deleteModal = useModal<ProductExternalReference>(
    'Confirmar Eliminación'
  )
  const viewModal = useModal<number>('Detalle de Marca PIM')

  const columnsConfig = getColumnsConfig()

  const requestData = (filters: Filter[], page: number, pageSize: number) => {
    callApi(
      getProductExternalReferences(filters, page, pageSize, orderBy)
    ).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const handleSorting = (
    field: keyof ProductExternalReference,
    direction: 'asc' | 'desc'
  ) => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const handleFilterChange = (value: React.SetStateAction<Filter[]>) => {
    let newFilters: Filter[]

    if (typeof value === 'function') {
      newFilters = value(selectedFilters)
    } else {
      newFilters = value
    }

    setSelectedFilters(newFilters)

    if (newFilters.length === 0 && selectedFilters.length > 0) {
      requestData([], pagination.page, pagination.pageSize)
    }
  }

  const handleCreate = (values: ProductExternalReference) => {
    callApi(createProductExternalReference(values))
      .then(() => {
        openSnackBar('Marca PIM creada correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        createModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleEdit = (values: ProductExternalReference) => {
    callApi(
      updateProductExternalReference(values.productExternalReferenceId, values)
    )
      .then(() => {
        openSnackBar('Marca PIM actualizada correctamente', true)
        requestData(selectedFilters, pagination.page, pagination.pageSize)
        editModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleDelete = (productExternalReferenceId: number) => {
    const currentTotal = state.data?.totalItems || 0

    callApi(deleteProductExternalReference(productExternalReferenceId))
      .then(() => {
        openSnackBar('Marca PIM eliminada correctamente', true)

        const newPage = calculatePageAfterDelete(
          pagination.page,
          pagination.pageSize,
          currentTotal,
          1
        )

        if (newPage !== pagination.page) {
          pagination.setPagination(newPage, pagination.pageSize)
        }

        requestData(selectedFilters, newPage, pagination.pageSize)
        deleteModal.closeModal()
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleCreateClick = () => {
    createModal.openModal('md')
  }

  // Corre una vez al montar (carga inicial) y de nuevo cuando cambia el ordenamiento
  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestData(selectedFilters, pagination.page, pagination.pageSize)
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={() =>
            requestData(selectedFilters, pagination.page, pagination.pageSize)
          }
          selectedFilters={selectedFilters}
          setSelectedFilters={handleFilterChange}
          includeSearchOnClean={true}
          onAddButtonClick={handleCreateClick}
          createButtonTooltip='Crear Marca PIM'
        />

        <GenericTableV2<ProductExternalReference>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='productExternalReferenceId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof ProductExternalReference,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'view',
              label: 'Ver',
              onClick: (item) =>
                viewModal.openModal('md', item.productExternalReferenceId),
              icon: <Visibility />
            },
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('md', item),
              icon: <Edit />
            },
            {
              key: 'delete',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
      >
        <ProductExternalReferenceForm
          onSubmit={handleCreate}
          closeModal={createModal.closeModal}
        />
      </Modal>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
      >
        <ProductExternalReferenceForm
          initialData={editModal.extendedProps}
          onSubmit={handleEdit}
          isEditing={true}
          closeModal={editModal.closeModal}
        />
      </Modal>

      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        loading={state.status === Status.Loading}
        successButton={{
          action: () =>
            handleDelete(
              deleteModal.extendedProps?.productExternalReferenceId || 0
            ),
          title: 'Confirmar'
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
      >
        ¿Desea eliminar la Marca PIM{' '}
        <strong>{deleteModal.extendedProps?.name}</strong>?
      </Modal>

      <Modal
        open={viewModal.isOpen}
        maxWidth={viewModal.size}
        title={viewModal.title}
        onClose={viewModal.closeModal}
        cancelButton={{
          action: viewModal.closeModal,
          title: 'Cerrar'
        }}
      >
        <ViewProductExternalReference
          productExternalReferenceId={viewModal.extendedProps || 0}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
